import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import styles from './GuestCom.module.css';

const ReservationTable = () => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState({ username: 'Admin User' });
  const [message, setMessage] = useState('');
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);

  const [bookingDetails, setBookingDetails] = useState({
    name: '',
    phoneNumber: '',
    checkInDateTime: '',
    checkOutDateTime: '',
    numberOfGuests: 1,
    roomType: 'Standard',
    mealPlan: 'Continental Plan (inc. Breakfast)',
    status: 'New',
    specialRequest: '',
  });

  const [reservations, setReservations] = useState([]);
  const [hotelBookings, setHotelBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [filterStatus, setFilterStatus] = useState('All Bookings');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const reservationsPerPage = 8;

  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [formData, setFormData] = useState({});

  const isEditMode = useMemo(() => currentReservation !== null, [currentReservation]);

  const [availableRoomsForSelector, setAvailableRoomsForSelector] = useState([]);

  const [statusCounts, setStatusCounts] = useState({
    'All Bookings': 0,
    'Booked': 0,
    'Reserved': 0,
    'Checked In': 0,
    'Checked Out': 0,
    'Cancelled': 0,
    'New': 0,
    'Pending': 0,
    'Paid': 0,
  });

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCreateBookingClick = () => {
    setShowBookingForm(true);
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
    setBookingDetails({
      name: '',
      phoneNumber: '',
      checkInDateTime: '',
      checkOutDateTime: '',
      numberOfGuests: 1,
      roomType: 'Standard',
      mealPlan: 'Continental Plan (inc. Breakfast)',
      status: 'New',
      specialRequest: '',
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails(prevDetails => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const showCustomMessage = (msg, isError = false) => {
    setMessage(msg);
    setShowMessageBox(true);
    setTimeout(() => {
      setShowMessageBox(false);
      setMessage('');
    }, 3000);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      name: bookingDetails.name,
      phone: bookingDetails.phoneNumber,
      check_in: bookingDetails.checkInDateTime,
      check_out: bookingDetails.checkOutDateTime,
      guests: parseInt(bookingDetails.numberOfGuests, 10),
      meal_plan: bookingDetails.mealPlan,
      room_type: bookingDetails.roomType,
      special_request: bookingDetails.specialRequest,
      status: bookingDetails.status,
    };

    try {
      const response = await fetch('https://admin.thepridehotel.in/api/hotel_bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Booking Submitted Successfully:', result);
        showCustomMessage("Booking request submitted successfully!");
        handleCloseBookingForm();
        fetchData();
      } else {
        const errorData = await response.json();
        console.error('Error submitting booking:', response.status, errorData);
        showCustomMessage(`Error: ${errorData.message || 'Failed to submit booking'}`, true);
      }
    } catch (error) {
      console.error('Network error or unexpected issue:', error);
      showCustomMessage("Error: Could not connect to the server. Please ensure your XAMPP server is running.", true);
    }
  };

  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || checkIn === 'N/A' || checkOut === 'N/A') {
      return 0;
    }
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diffTime = Math.abs(checkOutDate.getTime() - checkInDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const calculatePaymentStatus = (total, paid) => {
    const totalAmount = parseFloat(total);
    const paidAmount = parseFloat(paid);

    if (isNaN(totalAmount) || isNaN(paidAmount) || totalAmount === 0 || paidAmount === 0) {
      return 'Pending';
    }
    if (paidAmount >= totalAmount) {
      return 'Paid';
    }
    return 'Pending';
  };

  const fetchData = async () => {
    try {
      const guestResponse = await axios.get('https://admin.thepridehotel.in/api/guest');
      const formattedGuestData = guestResponse.data.map(item => ({
        id: item.id,
        reservationId: item.reservation_id,
        name: item.name,
        room: item.room_number,
        total: parseFloat(item.total_amount),
        paid: parseFloat(item.amount_paid),
        status: item.status,
      }));
      setReservations(formattedGuestData);

      const hotelBookingResponse = await axios.get('https://admin.thepridehotel.in/api/hotel_bookings');
      setHotelBookings(hotelBookingResponse.data);

      const roomsResponse = await axios.get('https://admin.thepridehotel.in/api/rooms');
      setRooms(roomsResponse.data);

      const inventoryResponse = await axios.get('https://admin.thepridehotel.in/api/inventory');
      setInventory(inventoryResponse.data);

      const newCounts = {
        'All Bookings': formattedGuestData.length,
        'Booked': formattedGuestData.filter(res => res.status === 'Booked').length,
        'Reserved': formattedGuestData.filter(res => res.status === 'Reserved').length,
        'Checked In': formattedGuestData.filter(res => res.status === 'Checked In').length,
        'Checked Out': formattedGuestData.filter(res => res.status === 'Checked Out').length,
        'Cancelled': formattedGuestData.filter(res => res.status === 'Cancelled').length,
        'New': formattedGuestData.filter(res => res.status === 'New').length,
        'Pending': formattedGuestData.filter(res => calculatePaymentStatus(res.total, res.paid) === 'Pending').length,
        'Paid': formattedGuestData.filter(res => calculatePaymentStatus(res.total, res.paid) === 'Paid').length,
      };
      setStatusCounts(newCounts);
    } catch (error) {
      console.error('Error fetching data:', error);
      window.alert('Failed to load data. Please ensure the backend server is running and tables exist.');
    }
  };

  const filteredAndSearchedReservations = useMemo(() => {
    let tempReservations = reservations.map(guestRes => {
      const linkedHotelBooking = hotelBookings.find(
        hb => String(hb.id) === String(guestRes.reservationId)
      );

      let calculatedTotal = guestRes.total;
      let roomPrice = 0;
      let numberOfNights = 0;

      if (linkedHotelBooking) {
        numberOfNights = calculateNights(linkedHotelBooking.check_in, linkedHotelBooking.check_out);
        const inventoryItem = inventory.find(inv => inv.room_type === linkedHotelBooking.room_type);

        if (inventoryItem && inventoryItem.price !== undefined) {
          roomPrice = parseFloat(inventoryItem.price);
          const baseTotal = roomPrice * numberOfNights;
          calculatedTotal = baseTotal;
        }
      }

      const displayPaymentStatus = calculatePaymentStatus(calculatedTotal, guestRes.paid);

      return {
        ...guestRes,
        bookingId: linkedHotelBooking ? linkedHotelBooking.id : 'N/A',
        bookingName: linkedHotelBooking ? linkedHotelBooking.name : 'N/A',
        bookingPhone: linkedHotelBooking ? linkedHotelBooking.phone : 'N/A',
        bookingCheckIn: linkedHotelBooking ? linkedHotelBooking.check_in : 'N/A',
        bookingCheckOut: linkedHotelBooking ? linkedHotelBooking.check_out : 'N/A',
        bookingGuests: linkedHotelBooking ? linkedHotelBooking.guests : 'N/A',
        bookingRoomType: linkedHotelBooking ? linkedHotelBooking.room_type : 'N/A',
        bookingMealPlan: linkedHotelBooking ? linkedHotelBooking.meal_plan : 'N/A',
        bookingSpecialRequest: linkedHotelBooking ? linkedHotelBooking.special_request : 'N/A',
        hotelBookingStatus: linkedHotelBooking ? linkedHotelBooking.status : 'N/A',
        displayPaymentStatus: displayPaymentStatus,
        total: calculatedTotal,
        originalRoomPrice: roomPrice,
        numberOfNights: numberOfNights,
      };
    });

    if (filterStatus !== 'All Bookings') {
      tempReservations = tempReservations.filter(res => {
        if (filterStatus === 'Pending' || filterStatus === 'Paid') {
          return res.displayPaymentStatus === filterStatus;
        }
        return res.status === filterStatus;
      });
    }

    if (searchTerm) {
      tempReservations = tempReservations.filter(res =>
        res.room.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.bookingName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    tempReservations.sort((a, b) => {
      const idA = a.bookingId === 'N/A' ? -1 : parseInt(a.bookingId);
      const idB = b.bookingId === 'N/A' ? -1 : parseInt(b.bookingId);
      return idB - idA;
    });

    return tempReservations;
  }, [reservations, hotelBookings, inventory, filterStatus, searchTerm, rooms]);

  const totalPages = Math.ceil(filteredAndSearchedReservations.length / reservationsPerPage);
  const currentReservations = filteredAndSearchedReservations.slice(
    (page - 1) * reservationsPerPage,
    page * reservationsPerPage
  );

  useEffect(() => {
    let roomTypeToFetch = '';
    if (isEditMode) {
      roomTypeToFetch = formData.booking_room_type;
    } else if (formData.reservationId) {
      const selectedBooking = hotelBookings.find(hb => String(hb.id) === String(formData.reservationId));
      if (selectedBooking) {
        roomTypeToFetch = selectedBooking.room_type;
      }
    }

    const fetchFilteredRooms = async (type) => {
      if (!type) {
        setAvailableRoomsForSelector([]);
        return;
      }
      try {
        const apiUrl = `https://admin.thepridehotel.in/api/rooms/filtered?status=Available&room_type=${type}`;
        const response = await axios.get(apiUrl);
        let fetchedRooms = response.data;
        if (isEditMode && currentReservation?.room) {
          const currentRoomNumber = currentReservation.room;
          const isCurrentRoomInFetchedList = fetchedRooms.some(room => room.room_number === currentRoomNumber);
          if (!isCurrentRoomInFetchedList) {
            const currentRoomObject = rooms.find(room => room.room_number === currentRoomNumber);
            if (currentRoomObject) {
              fetchedRooms = [currentRoomObject, ...fetchedRooms];
            }
          }
        }
        setAvailableRoomsForSelector(fetchedRooms);
      } catch (error) {
        console.error('Error fetching filtered rooms for selector:', error);
        setAvailableRoomsForSelector([]);
      }
    };
    fetchFilteredRooms(roomTypeToFetch);
  }, [formData.reservationId, formData.booking_room_type, currentReservation, hotelBookings, rooms, isEditMode]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Checked In':
        return { backgroundColor: '#E0F2FE', color: '#3B82F6' };
      case 'Checked Out':
        return { backgroundColor: '#FFE4E6', color: '#EF4444' };
      case 'Booked':
        return { backgroundColor: '#FFFBEB', color: '#F59E0B' };
      case 'Reserved':
        return { backgroundColor: '#EEF2FF', color: '#6366F1' };
      case 'Cancelled':
        return { backgroundColor: '#FEE2E2', color: '#DC2626' };
      case 'New':
        return { backgroundColor: '#D1FAE5', color: '#10B981' };
      case 'Pending':
        return { backgroundColor: '#FEF3C7', color: '#D97706' };
      case 'Paid':
        return { backgroundColor: '#D1FAE5', color: '#059669' };
      case 'Available':
        return { backgroundColor: '#D1FAE5', color: '#059669' };
      case 'Occupied':
        return { backgroundColor: '#FFFBEB', color: '#F59E0B' };
      default:
        return {};
    }
  };

  const handleEditClick = (reservation) => {
    setCurrentReservation(reservation);
    setFormData({
      id: reservation.id,
      reservationId: reservation.reservationId,
      name: reservation.name,
      room: reservation.room,
      total: parseFloat(reservation.total),
      paid: parseFloat(reservation.paid),
      status: reservation.status,
      booking_id: reservation.bookingId,
      booking_room_type: reservation.bookingRoomType,
      booking_status: reservation.hotelBookingStatus,
      booking_name: reservation.bookingName,
    });
    setAddEditModalOpen(true);
  };

  const handleDeleteClick = (reservation) => {
    setReservationToDelete(reservation);
    setDeleteConfirmModalOpen(true);
  };

  const handleAddClick = () => {
    setCurrentReservation(null);
    setFormData({
      reservationId: '',
      name: '',
      room: '',
      total: 0, // Corrected: Initialized to a number
      paid: 0,   // Corrected: Initialized to a number
      status: 'New',
      booking_id: '',
      booking_room_type: '',
      booking_status: 'New',
      booking_name: '',
    });
    setAddEditModalOpen(true);
  };

  const handleViewClick = (reservation) => {
    setCurrentReservation(reservation);
    setViewModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newState = { ...prev, [name]: value };

      if (isEditMode && name === 'booking_room_type') {
        newState.room = '';
        newState.booking_room_type = value;
      }
      else if (!isEditMode && name === 'reservationId') {
        const selectedBooking = hotelBookings.find(hb => String(hb.id) === String(value));
        if (selectedBooking) {
          newState.booking_room_type = selectedBooking.room_type;
          newState.booking_status = selectedBooking.status;
          newState.booking_name = selectedBooking.name;
          newState.booking_phone = selectedBooking.phone;
          newState.booking_check_in = selectedBooking.check_in;
          newState.booking_check_out = selectedBooking.check_out;
          newState.booking_guests = selectedBooking.guests;
          newState.booking_meal_plan = selectedBooking.meal_plan;
          newState.booking_special_request = selectedBooking.special_request;
        } else {
          newState.booking_room_type = '';
          newState.booking_status = '';
          newState.booking_name = '';
          newState.booking_phone = '';
          newState.booking_check_in = '';
          newState.booking_check_out = '';
          newState.booking_guests = '';
          newState.booking_meal_plan = '';
          newState.booking_special_request = '';
        }
        newState.room = '';
      }

      if (name === 'booking_status' || (name === 'reservationId' && !isEditMode)) {
        const currentBookingStatus = (name === 'booking_status' ? value : newState.booking_status);
        if (['Booked', 'Reserved', 'Checked In'].includes(currentBookingStatus)) {
          newState.status = 'Occupied';
        } else {
          newState.status = 'Available';
        }
      }

      return newState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { reservationId, name, room, total, paid, status, booking_id, booking_room_type, booking_status } = formData;
    
    // Updated validation check
    if (!name || !room || (typeof total !== 'number' && total === '') || (typeof paid !== 'number' && paid === '') || !status) {
      window.alert('Please fill in all required fields.');
      return;
    }
    
    if (!isEditMode && !reservationId) {
      window.alert('Please select a Booking ID.');
      return;
    }
    if (isNaN(parseFloat(total)) || parseFloat(total) < 0) {
      window.alert('Total Amount must be a non-negative number.');
      return;
    }
    if (isNaN(parseFloat(paid)) || parseFloat(paid) < 0) {
      window.alert('Amount Paid must be a non-negative number.');
      return;
    }

    try {
      if (isEditMode) {
        console.log("Updating existing reservation in DB:");
        await axios.put(`https://admin.thepridehotel.in/api/guest/${formData.id}`, {
          reservationId: formData.reservationId,
          name: formData.name,
          roomNumber: formData.room,
          totalAmount: parseFloat(formData.total),
          amountPaid: parseFloat(formData.paid),
          status: formData.status,
        });

        if (booking_id) {
          await axios.put(`https://admin.thepridehotel.in/api/hotel_bookings/${booking_id}`, {
            name: formData.booking_name,
            room_type: formData.booking_room_type,
            status: formData.booking_status,
          });
        }
        window.alert('Reservation and associated booking updated successfully!');
      } else {
        console.log("Adding new reservation to DB:");
        await axios.post('https://admin.thepridehotel.in/api/guest', {
          reservation_id: formData.reservationId,
          name: formData.name,
          room_number: formData.room,
          total_amount: parseFloat(formData.total),
          amount_paid: parseFloat(formData.paid),
          status: formData.status,
        });
        window.alert('Reservation added successfully!');
      }
      setAddEditModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving reservation:', error);
      window.alert('Failed to save reservation. ' + (error.response?.data?.error || error.message));
    }
  };

  const handleConfirmDelete = async () => {
    try {
      console.log("Deleting reservation from DB for ID:", reservationToDelete.id);
      await axios.delete(`https://admin.thepridehotel.in/api/guest/${reservationToDelete.id}`);
      window.alert('Reservation deleted successfully!');
      setDeleteConfirmModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting reservation:', error);
      window.alert('Failed to delete reservation.');
    }
  };

  const handleDownloadPdf = async () => {
    const input = document.getElementById('reservation-details-for-pdf');
    if (!input) {
      console.error("PDF target element not found!");
      return;
    }

    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`Reservation_${currentReservation.bookingId}.pdf`);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.headerTitle}>Guest Reservations</h2>
      <div className={styles.topControls}>
        <button
          className={`${styles.filterButton} ${filterStatus === 'All Bookings' ? styles.filterButtonActive : styles.filterButtonInactive}`}
          onClick={() => setFilterStatus('All Bookings')}
        >
          All Bookings ({statusCounts['All Bookings']})
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === 'Pending' ? styles.filterButtonActive : styles.filterButtonInactive}`}
          onClick={() => setFilterStatus('Pending')}
        >
          Pending Payment ({statusCounts['Pending']})
        </button>
        <button
          className={`${styles.filterButton} ${filterStatus === 'Paid' ? styles.filterButtonActive : styles.filterButtonInactive}`}
          onClick={() => setFilterStatus('Paid')}
        >
          Paid ({statusCounts['Paid']})
        </button>

        <input
          type="text"
          placeholder="Search by Room No., Guest Name, or Booking Name..."
          className={styles.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <div className="top-nav-row-two">
          <button className="booking-btn" onClick={handleCreateBookingClick}>
            Create booking
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Booking ID</th>
              <th className={styles.tableHeader}>Guest Name</th>
              <th className={styles.tableHeader}>Room Type</th>
              <th className={styles.tableHeader}>Room No.</th>
              <th className={styles.tableHeader}>Hotel Booking Status</th>
              <th className={styles.tableHeader}>Payment Status</th>
              <th className={styles.tableHeader}>Total Amount</th>
              <th className={styles.tableHeader}>Amount Paid</th>
              <th className={`${styles.tableHeader} ${styles.actionIcons}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentReservations.length > 0 ? (
              currentReservations.map((reservation) => (
                <tr key={reservation.id} className={styles.dataRow}>
                  <td className={styles.tableCell}>{reservation.bookingId}</td>
                  <td className={styles.tableCell}>{reservation.name}</td>
                  <td className={styles.tableCell}>{reservation.bookingRoomType}</td>
                  <td className={styles.tableCell}>{reservation.room}</td>
                  <td className={styles.tableCell}>
                    <span className={styles.status} style={getStatusStyle(reservation.hotelBookingStatus)}>
                      {reservation.hotelBookingStatus}
                    </span>
                  </td>
                  <td className={styles.tableCell}>
                    <span className={styles.status} style={getStatusStyle(reservation.displayPaymentStatus)}>
                      {reservation.displayPaymentStatus}
                    </span>
                  </td>
                  <td className={styles.tableCell}>₹ {reservation.total.toFixed(2)}</td>
                  <td className={styles.tableCell}>₹ {reservation.paid.toFixed(2)}</td>
                  <td className={`${styles.tableCell} ${styles.actionIcons}`}>
                    <button
                      onClick={() => handleViewClick(reservation)}
                      className={`${styles.actionButton} ${styles.actionButtonView}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    <button
                      onClick={() => handleEditClick(reservation)}
                      className={`${styles.actionButton} ${styles.actionButtonEdit}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                    </button>
                    <button
                      onClick={() => handleDeleteClick(reservation)}
                      className={`${styles.actionButton} ${styles.actionButtonDelete}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className={`${styles.tableCell}`}>No reservations found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page === 1}
          className={`${styles.paginationButton} ${page === 1 ? styles.paginationButtonDisabled : ''}`}
        >
          Previous
        </button>
        <div className={styles.pages}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pNum => (
            <span
              key={pNum}
              className={`${styles.pageNumber} ${pNum === page ? styles.pageNumberActive : ''}`}
              onClick={() => setPage(pNum)}
            >
              {pNum}
            </span>
          ))}
        </div>
        <button
          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          className={`${styles.paginationButton} ${page === totalPages ? styles.paginationButtonDisabled : ''}`}
        >
          Next
        </button>
      </div>

      {addEditModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button onClick={() => setAddEditModalOpen(false)} className={styles.modalCloseButton}>&times;</button>
            <h3>{currentReservation ? 'Edit Guest Details' : 'Add New Guest Booking'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label className={styles.modalLabel}>Booking ID:</label>
                {currentReservation ? (
                  <input
                    type="text"
                    name="booking_id"
                    value={formData.booking_id || ''}
                    className={styles.modalInput}
                    style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                    readOnly
                  />
                ) : (
                  <select
                    name="reservationId"
                    value={formData.reservationId || ''}
                    onChange={handleChange}
                    className={styles.modalSelect}
                    required
                  >
                    <option value="">Select a Booking ID</option>
                    {hotelBookings.map(hb => (
                      <option key={hb.id} value={hb.id}>
                        {hb.id} - {hb.name} ({hb.room_type})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className={styles.formColumns} style={{ gridTemplateColumns: viewportHeight > window.innerHeight * 0.8 ? '1fr 1fr' : '1fr' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Guest Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    className={styles.modalInput}
                    required
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Room Type (from Booking):</label>
                  <select
                    name="booking_room_type"
                    value={formData.booking_room_type || ''}
                    onChange={handleChange}
                    className={styles.modalSelect}
                    required
                    disabled={!currentReservation}
                  >
                    <option value="">Select Room Type</option>
                    {Array.from(new Set(rooms.map(r => r.room_type))).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Room Number:</label>
                  <select
                    name="room"
                    value={formData.room || ''}
                    onChange={handleChange}
                    className={styles.modalSelect}
                    required
                    disabled={!formData.reservationId && !currentReservation || availableRoomsForSelector.length === 0}
                  >
                    <option value="">Select a Room Number</option>
                    {availableRoomsForSelector.map(room => (
                      <option key={room.id} value={room.room_number}>
                        {room.room_number} ({room.bed_type} - {room.room_floor})
                      </option>
                    ))}
                  </select>
                  {((formData.reservationId || currentReservation) && availableRoomsForSelector.length === 0) && (
                    <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                      No rooms available for this room type.
                    </p>
                  )}
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Reservation Status:</label>
                  <select
                    name="booking_status"
                    value={formData.booking_status || 'New'}
                    onChange={handleChange}
                    className={styles.modalSelect}
                    required
                    disabled={!currentReservation}
                  >
                    <option value="New">New</option>
                    <option value="Booked">Booked</option>
                    <option value="Reserved">Reserved</option>
                    <option value="Checked In">Checked In</option>
                    <option value="Checked Out">Checked Out</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Total Amount:</label>
                  <input
                    type="number"
                    name="total"
                    value={formData.total || ''}
                    onChange={handleChange}
                    className={styles.modalInput}
                    required
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label className={styles.modalLabel}>Amount Paid:</label>
                  <input
                    type="number"
                    name="paid"
                    value={formData.paid || ''}
                    onChange={handleChange}
                    className={styles.modalInput}
                    required
                  />
                </div>
                {!currentReservation && (
                  <div style={{ marginBottom: '20px' }}>
                    <label className={styles.modalLabel}>Guest Status (Original):</label>
                    <select
                      name="status"
                      value={formData.status || 'New'}
                      onChange={handleChange}
                      className={styles.modalSelect}
                      required
                    >
                      <option value="New">New</option>
                      <option value="Booked">Booked</option>
                      <option value="Reserved">Reserved</option>
                      <option value="Checked In">Checked In</option>
                      <option value="Checked Out">Checked Out</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setAddEditModalOpen(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    backgroundColor: '#F9FAFB',
                    color: '#4B5563',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#AE7D54',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {currentReservation ? 'Update Details' : 'Add Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button onClick={() => setDeleteConfirmModalOpen(false)} className={styles.modalCloseButton}>&times;</button>
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the reservation for <strong>{reservationToDelete?.name} (Booking ID: {reservationToDelete?.bookingId})</strong>?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setDeleteConfirmModalOpen(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #D1D5DB',
                  backgroundColor: '#F9FAFB',
                  color: '#4B5563',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#EF4444',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModalOpen && currentReservation && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: '600px', padding: '30px' }}>
            <button onClick={() => setViewModalOpen(false)} className={styles.modalCloseButton}>&times;</button>
            <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '20px', color: '#AE7D54' }}>
              Reservation Details: {currentReservation.bookingId}
            </h3>

            <div id="reservation-details-for-pdf" style={{ padding: '0 20px' }}>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Booking ID:</span>
                <span>{currentReservation.bookingId}</span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Booking Name:</span>
                <span>{currentReservation.bookingName}</span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Room Type:</span>
                <span>{currentReservation.bookingRoomType}</span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Room Number:</span>
                <span>{currentReservation.room}</span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Hotel Booking Status:</span>
                <span className={styles.status} style={getStatusStyle(currentReservation.hotelBookingStatus)}>
                  {currentReservation.hotelBookingStatus}
                </span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Payment Status:</span>
                <span className={styles.status} style={getStatusStyle(currentReservation.displayPaymentStatus)}>
                  {currentReservation.displayPaymentStatus}
                </span>
              </div>
              <div className={styles.noteSection}>
                <p className={styles.noteTitle}>Booking Details:</p>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Phone:</span>
                  <span>{currentReservation.bookingPhone}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Check-in:</span>
                  <span>{currentReservation.bookingCheckIn ? new Date(currentReservation.bookingCheckIn).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Check-out:</span>
                  <span>{currentReservation.bookingCheckOut ? new Date(currentReservation.bookingCheckOut).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Guests:</span>
                  <span>{currentReservation.bookingGuests}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Meal Plan:</span>
                  <span>{currentReservation.bookingMealPlan}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Special Request:</span>
                  <span>{currentReservation.bookingSpecialRequest}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Room Price per Night:</span>
                  <span>₹ {currentReservation.originalRoomPrice.toFixed(2)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Number of Nights:</span>
                  <span>{currentReservation.numberOfNights}</span>
                </div>
              </div>

              <div className={styles.noteSection}>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Base Price (excl. GST):</span>
                  <span className={styles.totalValue}>₹ {(currentReservation.total / 1.12).toFixed(2)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>GST (12% included):</span>
                  <span className={styles.totalValue}>
                    ₹ {(currentReservation.total - currentReservation.total / 1.12).toFixed(2)}
                  </span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Total Amount (incl. GST):</span>
                  <span className={styles.totalValue}>₹ {currentReservation.total.toFixed(2)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Amount Paid:</span>
                  <span className={styles.totalValue}>₹ {currentReservation.paid.toFixed(2)}</span>
                </div>
              </div>

              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Balance Due:</span>
                <span className={styles.totalValue}>₹ {(currentReservation.total - currentReservation.paid).toFixed(2)}</span>
              </div>
            </div>

            <div className={styles.noteSection}>
              <p className={styles.noteTitle}>Hotel Policies:</p>
              <ul>
                <li>Suitable for children</li>
                <li>Credit/Debit cards are accepted</li>
                <li>Only Government ID (Aadhar Card and Govt. ID are accepted as ID proofs)</li>
                <li>PAN Card, Driving License and Office ID are not accepted as ID proofs)</li>
                <li>Pets are not allowed</li>
              </ul>
              <p className={styles.noteTitle}>Cancellation Policy:</p>
              <ul>
                <li>Free Cancellation is available before 1 day of arrival.</li>
                <li>No refund if cancelled within 1 day of arrival.</li>
              </ul>
            </div>
            <button
              className={styles.downloadButton}
              onClick={handleDownloadPdf}
            >
              Download as PDF
            </button>
          </div>
        </div>
      )}

      {showMessageBox && (
        <div className={`message-box ${message.includes('Error') ? 'error' : ''} show`}>
          {message}
        </div>
      )}

      {showBookingForm && (
        <div className={styles['booking-form-overlay']}>
          <div className={styles['booking-form-container']}>
            <h2>Create New Booking</h2>
            <form onSubmit={handleBookingSubmit}>
              <div className={styles['form-grid']}>
                <div className={styles['form-group']}>
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={bookingDetails.name}
                    onChange={handleFormChange}
                    required
                    className="rounded-lg"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={bookingDetails.phoneNumber}
                    onChange={handleFormChange}
                    required
                    className="rounded-lg"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="checkInDateTime">Check-in Date & Time</label>
                  <input
                    type="datetime-local"
                    id="checkInDateTime"
                    name="checkInDateTime"
                    value={bookingDetails.checkInDateTime}
                    onChange={handleFormChange}
                    required
                    className="rounded-lg"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="checkOutDateTime">Check-out Date & Time</label>
                  <input
                    type="datetime-local"
                    id="checkOutDateTime"
                    name="checkOutDateTime"
                    value={bookingDetails.checkOutDateTime}
                    onChange={handleFormChange}
                    required
                    className="rounded-lg"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="numberOfGuests">Number of Guests</label>
                  <input
                    type="number"
                    id="numberOfGuests"
                    name="numberOfGuests"
                    value={bookingDetails.numberOfGuests}
                    onChange={handleFormChange}
                    min="1"
                    required
                    className="rounded-lg"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="roomType">Room Type</label>
                  <select
                    id="roomType"
                    name="roomType"
                    value={bookingDetails.roomType}
                    onChange={handleFormChange}
                    className="rounded-lg"
                  >
                    <option value="Standard Room">Standard Room</option>
                    <option value="Executive Room">Executive Room</option>
                    <option value="Family Room">Family Room</option>
                    <option value="Twin Room">Twin Room</option>
                  </select>
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="mealPlan">Meal Plan</label>
                  <select
                    id="mealPlan"
                    name="mealPlan"
                    value={bookingDetails.mealPlan}
                    onChange={handleFormChange}
                    className="rounded-lg"
                  >
                    <option value="Continental Plan (inc. Breakfast)">Continental Plan (inc. Breakfast)</option>
                    <option value="European Plan (only stay)">European Plan (only stay)</option>
                  </select>
                </div>
                <div className={styles['form-group']}>
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={bookingDetails.status}
                    onChange={handleFormChange}
                    className="rounded-lg"
                  >
                    <option value="New">New</option>
                    <option value="Booked">Booked</option>
                    <option value="Checked In">Checked In</option>
                    <option value="Checked Out">Checked Out</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className={`${styles['form-group']} ${styles['full-width']}`}>
                  <label htmlFor="specialRequest">Special Request</label>
                  <textarea
                    id="specialRequest"
                    name="specialRequest"
                    value={bookingDetails.specialRequest}
                    onChange={handleFormChange}
                    rows="3"
                    className="rounded-lg"
                  ></textarea>
                </div>
              </div>
              <div className={styles['form-actions']}>
                <button type="button" className={`${styles['cancel-btn']} rounded-lg`} onClick={handleCloseBookingForm}>
                  Cancel
                </button>
                <button type="submit" className={`${styles['submit-btn']} rounded-lg`}>
                  Submit Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationTable;